/**
 * Simple staff queue for invoices@ pending_review rows.
 * Edit job, expense account, and phase, then approve (band chain) or reject.
 * Designer multi-line allocation popup on Project P&L still works for the same docs.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Check, FileText, Loader2, X } from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import QBDealPicker, { type QBDealOption } from '../components/sales-activity/QBDealPicker';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { useBillingPayablesAccess } from '../hooks/useBillingPayablesAccess';
import { canReviewDesignerInvoices } from '../utils/projectPLAccess';
import { getProjectDisplayName } from '../utils/projectName';
import {
  approveDesignerInvoice,
  formatInvoiceMoney,
  rejectDesignerInvoice,
  resolveDesignerInvoicePdfUrl,
  saveInvoiceReviewFields,
  subscribePendingReviewInvoices,
  type DesignerInvoiceDoc,
  type DesignerInvoicePhase,
} from '../utils/designerInvoiceService';
import BillingsHubModal from '../components/finances/BillingsHubModal';
import './InvoiceReviewPage.css';

const PHASES: { value: DesignerInvoicePhase; label: string }[] = [
  { value: 'estimating', label: 'Estimating' },
  { value: 'precon', label: 'Precon' },
  { value: 'under_contract', label: 'Under contract' },
  { value: 'overhead', label: 'Overhead' },
];

type Draft = {
  dealId: string | null;
  jobLabel: string;
  phase: DesignerInvoicePhase;
  accountId: string;
  accountName: string;
};

function draftFromInvoice(invoice: DesignerInvoiceDoc): Draft {
  const line = invoice.proposedLines[0];
  const phase =
    invoice.reviewPhase ||
    line?.phase ||
    invoice.suggestedPhase ||
    'estimating';
  const isOh = phase === 'overhead';
  return {
    dealId: isOh ? null : invoice.reviewDealId || line?.dealId || null,
    jobLabel: isOh
      ? 'Overhead'
      : invoice.reviewJobLabel ||
        invoice.suggestedJobLabel ||
        line?.dealLabel ||
        line?.projectLabel ||
        '',
    phase,
    accountId: invoice.reviewAccountId || invoice.suggestedAccountId || '',
    accountName: invoice.reviewAccountName || invoice.suggestedAccountName || '',
  };
}

const InvoiceReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { enhancedDeals, crmUsers, timberProjects } = useCRM();
  const { canManage } = useBillingPayablesAccess();
  const me = crmUsers.find((u) => u.uid === user?.id);
  const canReview = canReviewDesignerInvoices({
    authRole: user?.role,
    designations: me?.designations ?? user?.designations,
    briefingProfile: user?.executiveBriefing?.profile ?? me?.executiveBriefing?.profile ?? null,
  });

  const [invoices, setInvoices] = useState<DesignerInvoiceDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const dealOptions: QBDealOption[] = useMemo(() => {
    return enhancedDeals
      .filter((d): d is typeof d & { id: string } => Boolean(d.id))
      .map((d) => {
        const label = getProjectDisplayName(d) || d.title || d.id;
        const search = [d.projectNumber, d.clientLabel, d.descriptor, d.title, d.contactName, d.id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return { id: d.id, label: String(label), searchText: search };
      });
  }, [enhancedDeals]);

  useEffect(() => {
    if (!canReview && !canManage) {
      setLoading(false);
      return undefined;
    }
    return subscribePendingReviewInvoices((list) => {
      setLoading(false);
      setInvoices(list);
      setDrafts((prev) => {
        const next = { ...prev };
        for (const inv of list) {
          if (!next[inv.id]) next[inv.id] = draftFromInvoice(inv);
        }
        return next;
      });
      setSelectedId((cur) => {
        if (cur && list.some((i) => i.id === cur)) return cur;
        return list[0]?.id || null;
      });
    });
  }, [canReview, canManage]);

  const selected = invoices.find((i) => i.id === selectedId) || null;
  const draft = selected ? drafts[selected.id] || draftFromInvoice(selected) : null;

  useEffect(() => {
    if (!selected) {
      setPdfUrl(null);
      return;
    }
    let cancelled = false;
    setPdfLoading(true);
    void resolveDesignerInvoicePdfUrl(selected).then((url) => {
      if (!cancelled) {
        setPdfUrl(url);
        setPdfLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selected?.id, selected?.downloadUrl, selected?.storagePath]);

  const updateDraft = (id: string, patch: Partial<Draft>) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || draftFromInvoice(invoices.find((i) => i.id === id)!)), ...patch },
    }));
  };

  const persistDraft = async (invoice: DesignerInvoiceDoc, d: Draft) => {
    const project =
      d.dealId && d.phase !== 'overhead'
        ? timberProjects.find((p) => p.dealId === d.dealId && (p.phase || 'contracted') === (
            d.phase === 'precon' ? 'preconstruction' : d.phase === 'under_contract' ? 'contracted' : 'estimating'
          )) || timberProjects.find((p) => p.dealId === d.dealId)
        : null;
    await saveInvoiceReviewFields(invoice.id, {
      reviewDealId: d.phase === 'overhead' ? null : d.dealId,
      reviewProjectId: d.phase === 'overhead' ? null : project?.id || d.dealId,
      reviewJobLabel: d.phase === 'overhead' ? 'Overhead' : d.jobLabel || null,
      reviewPhase: d.phase,
      reviewAccountId: d.accountId || null,
      reviewAccountName: d.accountName || null,
    });
  };

  const handleApprove = async (invoice: DesignerInvoiceDoc) => {
    const d = drafts[invoice.id] || draftFromInvoice(invoice);
    setBusyId(invoice.id);
    setError(null);
    setNote(null);
    try {
      await persistDraft(invoice, d);
      const lines = invoice.proposedLines.map((line, index) => {
        if (invoice.proposedLines.length !== 1 && index > 0) return line;
        return {
          ...line,
          phase: d.phase,
          dealId: d.phase === 'overhead' ? null : d.dealId,
          projectId: d.phase === 'overhead' ? null : line.projectId || d.dealId,
          dealLabel: d.phase === 'overhead' ? null : d.jobLabel || line.dealLabel,
          projectLabel: d.phase === 'overhead' ? null : d.jobLabel || line.projectLabel,
        };
      });
      const result = await approveDesignerInvoice(invoice.id, lines);
      if (result.final === false) {
        setNote(
          typeof result.notes === 'string'
            ? result.notes
            : 'Approval recorded. Waiting on the next person in the approval chain.',
        );
      } else {
        setNote('Approved. QuickBooks bill is created on final approval only.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not approve this invoice.');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (invoice: DesignerInvoiceDoc) => {
    setBusyId(invoice.id);
    setError(null);
    setNote(null);
    try {
      await rejectDesignerInvoice(invoice.id, 'Rejected from invoice review');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reject this invoice.');
    } finally {
      setBusyId(null);
    }
  };

  if (authLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canReview && !canManage) {
    return (
      <div className="fin-hub">
        <AppHeader />
        <p className="ir-access-note">You don’t have access to invoice review.</p>
      </div>
    );
  }

  return (
    <div className="fin-hub">
      <AppHeader />
      <BillingsHubModal open onClose={() => navigate('/billing-payables')} />
      <div className="ir-main" hidden>
        <div className="ir-toolbar">
          <div>
            <p className="ir-back">
              <Link to="/billing-payables">← Billing &amp; Payables</Link>
            </p>
            <h1 className="fin-hub-title">Invoice review</h1>
            <p className="ir-sub">
              Invoices from invoices@ wait here. Check the job or overhead, expense account, and phase,
              then approve or reject.
            </p>
          </div>
          <span className="ir-count">
            {invoices.length} pending
          </span>
        </div>

        {error && <div className="ir-banner ir-banner--error">{error}</div>}
        {note && <div className="ir-banner ir-banner--note">{note}</div>}

        {loading ? (
          <div className="ir-empty">
            <Loader2 className="spin" size={18} /> Loading invoices…
          </div>
        ) : invoices.length === 0 ? (
          <div className="ir-empty">No invoices need review right now.</div>
        ) : (
          <div className="ir-layout">
            <div className="ir-list" role="list">
              {invoices.map((invoice) => {
                const d = drafts[invoice.id] || draftFromInvoice(invoice);
                const active = invoice.id === selectedId;
                return (
                  <button
                    key={invoice.id}
                    type="button"
                    role="listitem"
                    className={`ir-row${active ? ' is-active' : ''}`}
                    onClick={() => setSelectedId(invoice.id)}
                  >
                    <div className="ir-row-main">
                      <strong>{invoice.vendor || 'Vendor'}</strong>
                      <span>{formatInvoiceMoney(invoice.invoiceTotal)}</span>
                    </div>
                    <div className="ir-row-meta">
                      <span>{d.phase === 'overhead' ? 'Overhead' : d.jobLabel || 'No job yet'}</span>
                      <span>{d.accountName || d.accountId || 'No account yet'}</span>
                    </div>
                    <div className="ir-row-meta">
                      <span>#{invoice.invoiceNumber}</span>
                      <span>{PHASES.find((p) => p.value === d.phase)?.label || d.phase}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {selected && draft && (
              <div className="ir-detail">
                <div className="ir-pdf">
                  {pdfLoading ? (
                    <div className="ir-empty">
                      <Loader2 className="spin" size={18} /> Loading PDF…
                    </div>
                  ) : pdfUrl ? (
                    <iframe title={`Invoice ${selected.invoiceNumber}`} src={pdfUrl} />
                  ) : (
                    <div className="ir-empty">
                      <FileText size={18} /> PDF preview is not available.
                    </div>
                  )}
                </div>

                <div className="ir-form">
                  <h2>
                    {selected.vendor} · #{selected.invoiceNumber}
                  </h2>
                  <p className="ir-amount">{formatInvoiceMoney(selected.invoiceTotal)}</p>

                  <label className="ir-field">
                    <span>Job or overhead</span>
                    <QBDealPicker
                      value={draft.phase === 'overhead' ? '__overhead__' : draft.dealId || ''}
                      dealOptions={dealOptions}
                      emptyLabel="Choose a CRM job"
                      specialOptions={[{ id: '__overhead__', label: 'Overhead' }]}
                      onChange={(id) => {
                        if (id === '__overhead__' || !id) {
                          updateDraft(selected.id, {
                            phase: 'overhead',
                            dealId: null,
                            jobLabel: 'Overhead',
                          });
                          return;
                        }
                        const opt = dealOptions.find((o) => o.id === id);
                        updateDraft(selected.id, {
                          dealId: id,
                          jobLabel: opt?.label || draft.jobLabel,
                          phase:
                            draft.phase === 'overhead'
                              ? selected.suggestedPhase && selected.suggestedPhase !== 'overhead'
                                ? selected.suggestedPhase
                                : 'estimating'
                              : draft.phase,
                        });
                      }}
                    />
                  </label>

                  <label className="ir-field">
                    <span>Phase</span>
                    <select
                      value={draft.phase}
                      onChange={(e) =>
                        updateDraft(selected.id, {
                          phase: e.target.value as DesignerInvoicePhase,
                          ...(e.target.value === 'overhead'
                            ? { dealId: null, jobLabel: 'Overhead' }
                            : {}),
                        })
                      }
                    >
                      {PHASES.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    <span className="ir-hint">
                      Use the timber project phase, not the deal stage label.
                    </span>
                  </label>

                  <label className="ir-field">
                    <span>QuickBooks expense account</span>
                    <input
                      type="text"
                      value={draft.accountName}
                      placeholder="Account name (e.g. 223 Engineering COGS)"
                      onChange={(e) => updateDraft(selected.id, { accountName: e.target.value })}
                    />
                    <input
                      type="text"
                      value={draft.accountId}
                      placeholder="QuickBooks account id"
                      onChange={(e) => updateDraft(selected.id, { accountId: e.target.value })}
                    />
                  </label>

                  <div className="ir-actions">
                    <button
                      type="button"
                      className="ir-btn ir-btn--approve"
                      disabled={busyId === selected.id}
                      onClick={() => void handleApprove(selected)}
                    >
                      {busyId === selected.id ? <Loader2 className="spin" size={16} /> : <Check size={16} />}
                      Approve
                    </button>
                    <button
                      type="button"
                      className="ir-btn ir-btn--reject"
                      disabled={busyId === selected.id}
                      onClick={() => void handleReject(selected)}
                    >
                      <X size={16} />
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceReviewPage;
